import React, { Component } from 'react'
import Lightbox from 'react-image-lightbox';
import './App.css'
import withWorkflow from './components/withWorkflow';
import Modal from './components/Modal';
import { fetchPackingSlip, handoffToPatient } from './util/apiUtils';
import { getDateFromIso8601String } from './components/DatePicker';
import MedicareDiscardAlert from './components/MedicareDiscardAlert';
import workflowUtil from './util/workflowUtil';
import { buildPatientInfoFormFromOrder, validatePatientInfoForm } from './patient/PatientInfoForm';
import DateOfBirthInput from './components/DateOfBirthInput';
import {isDomesticRequisitionType, isResubmittalOrder, isUKOrder, getCustomerCareNumber} from './util/gearOrderUtil';
import PITLSummary from './phsycian-in-the-loop/PITLSummary';
import AlertMessage from './AlertMessage';

class SubmissionSummary extends Component {
	
	constructor(props) {
		super(props);

		const patient = this.props.gearOrder.patientInfo || {};

		// SCR-5651 Show Medicare reminder
		let showMedicareReminder = false;
		if (
			this.props.userInfo.type === 'myGDX' &&
			this.props.gearOrder.requisitions &&
			this.props.gearOrder.requisitions.find((req) => {
				return (
					req.billingMethod === process.env.REACT_APP_BILLING_INSURANCE_VALUE ||
					req.billingMethod === process.env.REACT_APP_BILLING_MEDICARE_VALUE
				);
			})
		) {
			showMedicareReminder = true;
		}

		this.state = {
			displayPackingSlip: false, // When true, shows the packing slip overlay
			packingSlipOverlayStatus: null, // A status message to show while a modal is waiting to load a packing slip
			handoffForm: {
				email: { value: patient.email || '' },
				dateOfBirth: { value: patient.dateOfBirth || '' }
			},
			paperless: false,
			showReqImage: false,
			showMedicareReminder: showMedicareReminder
		};
		
		this.sampleCollectionRef = React.createRef();
	}
	complete(){
		const isUKPatientSubmittingDropShipOrder = isUKOrder(this.props.gearOrder) && this.props.userInfo.type === 'prc'
													&& !(this.props.gearOrder?.requisitions?.some(req => req.reqNumber && req.reqNumber > 0));
		this.props.setGearOrder(null);
		if(isUKPatientSubmittingDropShipOrder){
			this.props.history.push({ pathname: '/uk/order' });
		} else {		
			this.props.history.push({pathname:'/'});
		}
	}
	componentDidMount() {
		for (let req of this.props.gearOrder.requisitions) {
			if (req.paperless) {
				this.setState(() => ({
					paperless: true
				}));
			}
		}
	}

	/**
	 * Update state to show the packing slip modal for a bulk order.
	 * Fetch the required packing slip data if it has not already been loaded.
	 *
	 * @param event The HTML event that triggered this method
	 */
	displayPackingSlip = (event) => {
		event.preventDefault();
		this.setState({
			displayPackingSlip: true,
			packingSlipOverlayStatus: null
		});

		if( this.state.packingSlip ) {
			return;
		}

		this.setState({
			packingSlipOverlayStatus: <i className="fa fa-spinner fa-spin fa-fw" style={{ fontSize: '5em' }}></i>
		});

		fetchPackingSlip( this.props.gearOrder.id, this.props.gearOrder.orderFormId )
			.then(text => {
				this.setState({
					packingSlip: text,
					packingSlipOverlayStatus: null
				});
			})
			.catch(e => {
				let errorMessage = e.message;
				if (!errorMessage) {
					errorMessage = 'An unexpected error has occurred. Please call us at ' + getCustomerCareNumber(this.props?.gearOrder) + '.';
				}
				this.setState({
					packingSlipOverlayStatus: <span className="text-danger">{errorMessage}</span>
				});
			});
	}

	/**
	 * Store the new value and 'valid' flag in state anytime the handoff email field is changed.
	 * The 'valid' flag will be set to true when the value is truthy, false otherwise.
	 *
	 * @param event The HtmlEvent that triggered the change
	 * @param event.target The HtmlEvent's target
	 * @param event.target.value The email input field's value
	 */
	handleEmailChange = ({ target: { value } }) => {
		this.setState( (prevState, props) => ({
			handoffForm: {
				...prevState.handoffForm, // Include everyting in handoffForm except 'email'
				email: {
					value: value,
					valid: !!value
				}
			}
		}));
	}

	/**
	 * Store the new value and 'valid' flag in state anytime the handoff date of birth field is changed.
	 * The 'valid' flag will be set to true when the value is truthy, false if the value is empty or not a valid date.
	 *
	 * @param dateStr The date of birth value in 'yyyy-mm-dd' format
	 */
	handleDateOfBirthChange = (dateStr) => {
		let error;
		if (dateStr) {
			try {
				// Try to parse the date and handle an error
				getDateFromIso8601String(dateStr);
			} catch (err) {
				error = `${dateStr} is not a valid date format. Please use YYYY-MM-DD or MM/DD/YYYY.`;
			}
		}
		this.setState((prevState, props) => ({
			handoffForm: {
				...prevState.handoffForm, // Include everyting in handoffForm except 'dateOfBirth'
				dateOfBirth: {
					value: dateStr,
					valid: !!dateStr && !error,
					error: error
				}
			}
		}));
	}

	/**
	 * Send the patient a handoff email and zipCode
	 */
	sendHandoff = () => {
		if (!this.state.handoffForm.email.value || !this.state.handoffForm.dateOfBirth.value) {
			// Show a validation error
			this.setState((prevState, props) => ({
				handoffForm: {
					email: {
						value: prevState.handoffForm.email.value,
						valid: !!prevState.handoffForm.email.value
					},
					dateOfBirth: {
						value: prevState.handoffForm.dateOfBirth.value,
						valid: !!prevState.handoffForm.dateOfBirth.value
					}
				}
			}));
		} else {
			// Show a spinner while sending.
			this.setState((prevState, props) => ({
				handoffForm: {
					...prevState.handoffForm, // Include everyting in handoffForm except 'email'
					email: {
						...prevState.handoffForm.email, // Include everything in email exception 'success', 'error', and 'loading'
						success: false,
						error: false,
						loading: true
					}
				}
			}));

			let patientInfo = this.props.gearOrder.patientInfo || {};
			patientInfo.dateOfBirth = this.state.handoffForm.dateOfBirth.value;
			patientInfo.email = this.state.handoffForm.email.value;

			// Send the email
			handoffToPatient(this.props.gearOrder.id, patientInfo)
				.then(gearOrder => {
					this.props.setGearOrder(gearOrder);
					// Show success message. Copy the handoffForm and change the email success and error values.
					this.setState({
						handoffForm: {
							...this.state.handoffForm,
							email: {
								...this.state.handoffForm.email,
								success: true,
								error: false
							}
						}
					});
				})
				.catch(e => {
					// Show error message. Copy the handoffForm and change the email success and error values.
					this.setState({
						handoffForm: {
							...this.state.handoffForm,
							email: {
								...this.state.handoffForm.email,
								success: false,
								error: (
									<span>
										The email failed to send. Please verify the email address is correct.
										<br />
										If you need assistance please call us at ${getCustomerCareNumber(this.props?.gearOrder)}.
									</span>
								)
							}
						}
					});
				})
				.finally(() => {
					// Clear the spinner
					this.setState((prevState, props) => ({
						handoffForm: {
							...prevState.handoffForm,
							email: {
								...prevState.handoffForm.email,
								loading: false
							}
						}
					}));
				});
		}
	}

	showReqImage = (e) => {
		e.preventDefault();
		this.setState({
			showReqImage: true
		});
	}

	/**
	 * Opens an electronic requisition PDF.
	 */
	viewOrder = (event) => {
		event.preventDefault();
		fetch().then(() => {
					// SCR-7772: Added the dateOfBirth as param for viewing orders in the case that a PRC users grabs an ereq.
        	window.open(process.env.REACT_APP_GEAR_REST_URL + "/packing-slip/order/" + this.props.gearOrder.id + "?dateOfBirth=" + this.props.gearOrder.patientInfo?.dateOfBirth, "_blank");
        });
	}

	render() {
		if (this.props.gearOrder.physicianInTheLoop) {
			return <PITLSummary
				userInfo={this.props.userInfo}
				gearOrder={this.props.gearOrder}
				setLoading={this.props.setLoading}
				setGearOrder={this.props.setGearOrder}
			/>
		}
		const domesticRequisitionType = isDomesticRequisitionType(this.props.gearOrder);

		// Get a list of all steps that the user has access to that are not 'Completed' or 'N/A'
		const incompleteSteps = workflowUtil.getIncompleteUserSteps(this.props.gearOrder, this.props.userInfo);

		// Only show the confirmation code if all steps are complete (Always show for international)
		let confirmationCode = (!domesticRequisitionType || !incompleteSteps.length) && this.props.gearOrder.id;
		let sampleCollectionStep = this.props.getWorkflowStep('collection');
		let authorizingPractitionerId = this.props.gearOrder.practitioner.authorizingDoctorGsdlNo;
		let authorizingPractitioner = this.props.userInfo.gsdlNo === this.props.gearOrder.practitioner.authorizingDoctorGsdlNo;
		let header = "Success";
		let message = null;
		if ( this.props.userInfo.type==="myGDX" && !authorizingPractitionerId) {
			header = "Thank you for demoing our Online Test Ordering system.";
    	} else if (this.props.userInfo.type==="myGDX" && authorizingPractitionerId && !authorizingPractitioner && !this.props.gearOrder.practAgreement) {
			header = "Authorization Needed";
		} else if (this.props.userInfo.type === "prc") {
			message = (
				<>
					<p className="text-default text-center">
						<strong>Thank you for activating your test online.</strong>
					</p>
					<p className="text-default text-center">
						{isUKOrder(this.props.gearOrder) ? (
							<>
								If you have questions about your order call Customer Support at {process.env.REACT_APP_UK_CUSTOMER_CARE_NUMBER} or email us at <a href={`mailto:${process.env.REACT_APP_UK_CUSTOMER_CARE_EMAIL}`}>{process.env.REACT_APP_UK_CUSTOMER_CARE_EMAIL}</a>.
							</>
						) : (
							<>
								If you supplied payment information using a credit card, it will be charged shortly after your collection pack is received by the laboratory.  If your insurance is in-network with our laboratory, then payment will be charged after a claim is processed with your insurance.
							</>
						)}
					</p>
				</>
			);
		} else {
			message = (<p className="text-default text-center">
				<strong>Thank you for submitting your order.</strong>
			</p>);
		}
		// Check if any sample collection step is incomplete
		let collectionIncomplete = sampleCollectionStep && this.props.gearOrder.workflow.steps.filter( step => step.id === sampleCollectionStep.id ).some( step => !step.completed );
		let hasReqNumber = this.props.gearOrder?.requisitions?.some(req => req.reqNumber && req.reqNumber > 0);
		const sampleCollectionNotRequired = this.props.userInfo.type === 'prc' && !hasReqNumber;
		collectionIncomplete = sampleCollectionNotRequired ? false : collectionIncomplete;
		
		// Find all drop-ship requisition item names
		let dropShipItems = this.props.gearOrder.requisitions
			.filter(req => req.dropShip)
			.map(req => req.omsOrderableItemName);

		const showDropShipItems = (dropShipItems.length > 0 && this.props.userInfo.type === "myGDX" && authorizingPractitionerId);

		// Find all handed-off, non-dropship requisition ids and item names
		let handoffItems = this.props.gearOrder && this.props.gearOrder.requisitions
			.filter(req => !req.dropShip && !this.props.isRequisitionComplete(req.id))
			.map(req => "Requisition " + req.reqNumber + " - " + req.omsOrderableItemName);

		const validatedPatientInfoForm = validatePatientInfoForm(buildPatientInfoFormFromOrder(this.props.gearOrder), this.props.gearOrder, true);
		let incompleteClientRegistration = (!validatedPatientInfoForm || Object.values(validatedPatientInfoForm).some((prop) => !prop.valid)) && this.props.userInfo.type === "myGDX";

		const showHandOffItems = (handoffItems.length > 0 && this.props.userInfo.type === "myGDX");
		const disableHandoff = !this.state.handoffForm.email.value || !this.state.handoffForm.dateOfBirth.value;

		const noCardActivation = this.props.gearOrder.containsNoCardActivation;

		// SCR-4808 Always show the eReq button for myGDX users.
		let showElectronicRequisitionButton = this.props.userInfo.type === 'myGDX';
		let showElectronicRequisitionButtonPrc = this.props.userInfo.type === 'prc' && (noCardActivation || !incompleteSteps.length);
		let eReqButton;
		if (showElectronicRequisitionButton || showElectronicRequisitionButtonPrc) {
			eReqButton =
					<a target="_blank" rel="noopener noreferrer" href={process.env.REACT_APP_GEAR_REST_URL + "/packing-slip/order/" + this.props.gearOrder.id + "?dateOfBirth=" + this.props.gearOrder.patientInfo?.dateOfBirth} onClick={this.viewOrder}>
						<button className="btn btn-primary rounded-pill btn-sm margin-clear" >Electronic Requisition (opens a new tab)<i className="fa fa-print ps-2"></i></button>
					</a>
		}


		const simplifiedClientBilling = this.props.gearOrder.simplifiedClientBilling;

		const activationCardOrder = this.props.gearOrder?.requisitions?.some(req => req.activationCode);

		const bulkOrder = this.props.gearOrder && this.props.gearOrder.containsShipToOfficeRequisition && this.props.userInfo.type === "myGDX";
		
		if( bulkOrder && authorizingPractitionerId) {
			return (
				<div className="container">
					{this.state.displayPackingSlip && (
						<Modal
							id={
								(this.props.gearOrder.orderFormId ? this.props.gearOrder.orderFormId : '') +
								'-packing-slip-modal'
							}
							title={'Order Information'}
							confirmAction={'Close'}
							modalHidden={() => this.setState({ displayPackingSlip: false })}
						>
							{!this.state.packingSlip && (
								<div className="text-center">{this.state.packingSlipOverlayStatus}</div>
							)}
							{this.state.packingSlip && <pre>{this.state.packingSlip}</pre>}
						</Modal>
					)}
					<br />
					<br />
					<div className="col-lg-8 offset-lg-2 col-md-8 offset-md-2">
						<div className="panel panel-primary" style={{"padding": "1em"}}>
							<div className="call-to-action">
								<h1 className="title text-center">Success</h1>
							</div>
							<p className="text-default text-center">
								<strong>Thank you for submitting your order.</strong>
							</p>
							<div className="alert alert-warning">
								<p>
									The collection packs in this order are for office inventory. <a href="https://www.gdx.net/activate" target="_blank" rel="noopener noreferrer">Activate each pack</a> before distributing to patients.
								</p>
								<p>Please note: Specimen labels include a unique ID used for activation. Labels must remain with the collection pack they are shipped with and are not interchangeable among packs.</p>
							</div>
							<p className="text-default text-center">
								{isUKOrder(this.props.gearOrder) ? (
									<strong>
										If you have questions about your order call Customer Support at {process.env.REACT_APP_UK_CUSTOMER_CARE_NUMBER} or email us at <a href={`mailto:${process.env.REACT_APP_UK_CUSTOMER_CARE_EMAIL}`}>{process.env.REACT_APP_UK_CUSTOMER_CARE_EMAIL}</a>.
									</strong>
								) : (
									<strong>If you have questions about your order call Customer Support at ${getCustomerCareNumber(this.props?.gearOrder)} or use our&nbsp;
										<a href="/mygdx/contact" target="_blank" rel="noopener noreferrer">online contact form</a>.
									</strong>
								)}
							</p>

							{this.props.gearOrder.orderFormId && (
								<div className="text-default text-center">
									<p>
										<strong>An electronic version of the packing slip is available below.</strong>
									</p>
									<button
										onClick={this.displayPackingSlip}
										className="btn btn-primary rounded-pill btn-sm margin-clear"
									>
										Electronic Packing Slip<i className="fa fa-print ps-2"></i>
									</button>
								</div>
							)}
						</div>
					</div>
					<div className="col-md-12">
						<button className="btn btn-white rounded-pill btn-sm margin-clear" onClick={() =>this.props.updateStep(this.props.gearOrder, 'summary', 'Back')}>
							<i className="pe-2 fa fa-arrow-circle-left"></i>
							Back
						</button>
						<button className="btn btn-primary rounded-pill btn-sm margin-clear pull-right" onClick={() =>this.complete()}>
							Start New Order
						</button>
					</div>
				</div>
			);
		}

		return(
			<div className="container">
				<br/>
				<br/>
				<div className="col-lg-8 offset-lg-2 col-md-8 offset-md-2">
					<div className="panel panel-primary" style={{"padding": "1em"}}>
						<div className="call-to-action">
							<h1 className="title text-center">
								{header}
							</h1>
						</div>
						{(this.props.userInfo.type === "prc" && this.props.gearOrder.practAgreementDate === null && this.state.paperless && !isResubmittalOrder(this.props.gearOrder)) &&
								<div className="alert alert-warning">
									<p>
										Your healthcare provider has not submitted an order for this test. Collection packs received by our lab without an authorized order may result in incomplete processing, discarded specimens, and delays. We encourage you to reach out to your healthcare provider to activate your test.
									</p>
								</div>
						}
						{message}
						{(this.props.userInfo.type === "prc") && (
							<MedicareDiscardAlert gearOrder={this.props.gearOrder} />
						)}
						{(confirmationCode ) && 
							<div className="text-default text-center">
								<p>
									<strong> Confirmation Code: </strong> {confirmationCode}
								</p>
								{/* Display the confirmation code explanaition for prc users or mygdx non-handoff orders*/}
								{(!simplifiedClientBilling && !noCardActivation && (this.props.userInfo.type === "prc" || (!dropShipItems.length && !handoffItems.length))) &&
									<>
										<p>Please write the confirmation code on your requisition form
											{activationCardOrder && <span> or activation card</span>}
											{' '}and include it when mailing your collection pack.
										</p>
										<p>
											<button type="button" className="btn btn-link btn-unstyled" onClick={this.showReqImage}><em>Where do I write my confirmation code?</em></button>
										</p>
									</>
								}
								{simplifiedClientBilling && (
									<p>Please write the confirmation code on your requisition card and include the card when returning your collection pack.</p>
								)}
							</div>
						}
						{noCardActivation && (
							<>
								{this.props.userInfo.type !== 'prc' && (
									<AlertMessage type="warning">
										<h4>Action Needed</h4>
										<ol>
											<li>
												Please print the eReq provided below and include it with the samples so
												we may successfully process your order.
											</li>
											<li>
												Your patient will need to write their first and last{' '}
												<strong>legal</strong> name on the specimen labels.
											</li>
										</ol>
									</AlertMessage>
								)}
								{this.props.userInfo.type === 'prc' && (
									<AlertMessage type="warning">
										<h4>Action Needed</h4>
										<ol>
											<li>
												Please return your specimens with the printed requisition provided by
												your provider. If your provider did not provide a paper copy, you may
												print and include the one below.
											</li>
											<li>
												Please clearly write your first and last <strong>legal</strong> name on
												the specimens to ensure the successful processing of your order.
											</li>
										</ol>
									</AlertMessage>
								)}
							</>
						)}
						{(this.props.userInfo.type === "prc" && collectionIncomplete) &&
							<div className="alert alert-danger" role="alert">
								<h4>Sample Collection Incomplete</h4>
								<strong>Please return to document the date of final sample collection when it has been completed.</strong>
							</div>
						}
						{this.props.userInfo.type==="myGDX" && authorizingPractitionerId && !authorizingPractitioner && !this.props.gearOrder.practAgreement &&
							<div className="mb-20 text-center">
								Thank you for completing your part of the activation process! Please proceed with the patient's sample collection and return samples to the lab for processing. In the meantime, we will contact the authorization provider for their signature.
							</div>
						}
						{showHandOffItems &&
							<>
								<span className="text-default">
									The following tests have been registered but are missing information that will need to be provided by the patient:
								</span>
								<ul className="text-default">
									{handoffItems.map(item => <li key={item}>{item}</li>)}
								</ul>
							</>
						}
						{!showHandOffItems && incompleteClientRegistration &&
							<>
								<span className="text-default">
									We have received your registration, but we are missing information that will need to be provided by the patient.
								</span>
							</>
						}
						{showDropShipItems &&
							<>
								<span className="text-default">
									Genova Diagnostics will ship the following collection packs to the patient:
								</span>
								<ul className="text-default">
									{dropShipItems.map(item => <li key={item}>{item}</li>)}
								</ul>
							</>
						}
						{/* Handoff email section */}
						{(domesticRequisitionType && (showHandOffItems || showDropShipItems || incompleteClientRegistration)) &&
							<div className="row panel-body">
								<p className="text-default col-12" style={{ marginBottom: '5px' }}>Genova will be happy to email your patient with instructions to complete online registration.  If you would like us to do this please provide patient email and DOB.</p>
								<div className={"form-group col-sm-6 mb-1 " + (this.state.handoffForm.email.valid === false ? "has-error" : "")}>
									<div>
										<label className="control-label" htmlFor="patient-email">Patient's Email</label>
										<input className="form-control" id="patient-email" maxLength="100" type="text" defaultValue={this.state.handoffForm.email.value} onChange={this.handleEmailChange} data-fieldname="email"></input>
										{this.state.handoffForm.email.valid === false && <span className="validation-message text-danger">Please provide an email address.</span>}
									</div>
								</div>
								<div className={"form-group col-sm-6 mb-1 " + (this.state.handoffForm.dateOfBirth.valid === false ? "has-error" : "")}>
									<div>
										<label className="control-label" htmlFor="dateOfBirth">Date Of Birth</label>
										<DateOfBirthInput value={this.state.handoffForm.dateOfBirth.value} onChange={this.handleDateOfBirthChange} displayDateString={true} displayAge={false} hideInputLables={true} isUKOrder={isUKOrder(this.props.gearOrder)} />
										{this.state.handoffForm.dateOfBirth.valid === false && <span className="validation-message text-danger" style={{display: 'block'}}>{this.state.handoffForm.dateOfBirth.error || 'Please provide a date of birth.'}</span>}
									</div>
								</div>
								{this.state.handoffForm.email.success && <span className='text-success col-12'><i className="fa fa-check-circle me-1"></i>An email has been sent to your patient.</span>}
								{this.state.handoffForm.email.loading && <small><span className="ms-2 text-info col-12">Sending... <i className="fa fa-spinner fa-spin fa-fw"></i></span></small>}
								<div className={"form-group col-12"}>
									<label className="control-label" htmlFor="patient-email">{' '}</label>
									<button className="btn btn-primary rounded-pill btn-sm pull-right" style={{ margin: '0', marginTop: '5px' }} onClick={() => this.sendHandoff()} disabled={disableHandoff} title={ disableHandoff ? 'Please provide an email address and date of birth' : ''}>
										Send Email
									</button>
								</div>
								{this.state.handoffForm.email.error &&
									<div className='col-12'>
										<span className='text-danger'><i className="fa fa-times-circle"></i>{this.state.handoffForm.email.error}</span>
									</div>
								}
							</div>
						}
						{showElectronicRequisitionButton &&
							<div className="text-default text-center space-bottom">
								{(authorizingPractitionerId && !showDropShipItems && !noCardActivation) && 
									<>
										<strong>
											Remember to include the patient-signed Genova Diagnostics paper requisition
											{activationCardOrder && <span> or completed activation card</span>}
											{' '}in the collection pack so we know whose it is!
										</strong>
										<br />
										<div className="space"></div>
									</>
								}
								<strong>An electronic version of the requisition is available below.</strong>
								<div className="space"></div>
								{eReqButton}
							</div>
						}
						{showElectronicRequisitionButtonPrc &&
							<div className="text-default text-center space-bottom">
								<strong>An electronic version of the requisition is available below.</strong>
								<div className="space"></div>
								{eReqButton}
							</div>
						}
						{this.state.showMedicareReminder &&
							<div>
							<p>
								<strong>
									Important: Remember to document the test name, medical necessity, and sign and
									date the medical record and/or progress notes.
								</strong>
							</p>
							<p>
								Medicare and other insurance carriers require that the physician clearly indicate
								all tests to be performed when completing progress notes (for example, “run labs”
								does not support an intent to order laboratory tests). Your documentation in the
								patient’s medical record must support the medical necessity for ordering the test(s)
								per Medicare regulations.
							</p>

							<p>
								Medicare and other insurance carriers conduct regular audits to ensure that the
								progress notes and/or medical records are properly documented, and these must be
								submitted upon request. Your documentation is essential to demonstrate the patient’s
								need for the tests, and therefore compliance with insurance billing guidelines.
							</p>
						</div>
						}
					</div>
				</div>
				<div className="col-md-12">
					<button className="btn btn-white rounded-pill btn-sm margin-clear" onClick={() =>this.props.updateStep(this.props.gearOrder, 'summary', 'Back')}>
						<i className="pe-2 fa fa-arrow-circle-left"></i> 
						Back
					</button>
					<button className="btn btn-primary rounded-pill btn-sm margin-clear pull-right" onClick={() =>this.complete()}>
						Start New Order
					</button>
				</div>
				{this.state.showReqImage &&
					<Lightbox mainSrc="/images/gear/req-confirmation-code.png"
								imageTitle="Genova Diagnostics Requisition Form"
								onCloseRequest={() => this.setState({ showReqImage: false })}
								imageLoadErrorMessage="Requisition image failed to load"/>
				}
		  </div>
		)
    }
}

// wrap the component in the workflow HOC to get access to common workflow logic
export default withWorkflow(SubmissionSummary);
